import { useCallback, useEffect, useRef, useState } from "react";
import {
  acceptPathologyOverlayCall,
  createPathologyDiseaseCall,
  deletePathologyDiseaseCall,
  generatePathologyOverlayCall,
  getPathologyOverlayCall,
  listPathologyDiseasesCall,
  listPathologyOverlaysCall,
  regeneratePathologyOverlayCall,
  updatePathologyDiseaseCall,
  deletePathologyOverlayCall,
} from "../../../redux/api/pathology.api";
import {
  boundsToJson,
  capturePixelsFromBounds,
  leafletBoundsToPixelRoi,
  slideMaxNativeZoom,
} from "./pathologyRoiUtils";
import { normalizePathologyOverlay } from "./pathologyOverlayUtils";

export function usePathologyAdmin({
  enabled,
  slideId,
  mapRef,
  geoJson,
}) {
  const [diseases, setDiseases] = useState([]);
  const [selectedDiseaseId, setSelectedDiseaseId] = useState("");
  const [overlays, setOverlays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newSeverity, setNewSeverity] = useState("mild");

  const [roiDrawActive, setRoiDrawActive] = useState(false);
  const [roiBounds, setRoiBounds] = useState(null);

  const [activeOverlay, setActiveOverlay] = useState(null);
  const [previewOpacity, setPreviewOpacity] = useState(0.85);
  const [customPrompt, setCustomPrompt] = useState("");
  const [pinDraft, setPinDraft] = useState({
    name: "",
    description: "",
    disease_label: "",
    severity: "mild",
  });
  const pollRef = useRef(null);

  const maxZoom = slideMaxNativeZoom(geoJson);
  const selectedDisease = diseases.find((d) => d.id === selectedDiseaseId);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshDiseases = useCallback(async () => {
    if (!enabled || !slideId) return;
    try {
      const res = await listPathologyDiseasesCall(slideId);
      if (res?.status && res.status >= 400) {
        setError(res?.message || "Failed to load diseases");
        return;
      }
      const list = res?.diseases || [];
      setDiseases(list);
      setSelectedDiseaseId((prev) => {
        if (prev && list.some((d) => d.id === prev)) return prev;
        return list[0]?.id ?? "";
      });
    } catch (e) {
      setError(e?.message || "Failed to load diseases");
    }
  }, [enabled, slideId]);

  const refreshOverlays = useCallback(
    async (diseaseId) => {
      if (!enabled || !slideId || !diseaseId) {
        setOverlays([]);
        return;
      }
      try {
        const res = await listPathologyOverlaysCall({ slideId, diseaseId });
        setOverlays(
          (res?.overlays || [])
            .map(normalizePathologyOverlay)
            .filter(Boolean)
        );
      } catch (e) {
        setError(e?.message || "Failed to load overlays");
      }
    },
    [enabled, slideId]
  );

  useEffect(() => {
    if (enabled) refreshDiseases();
  }, [enabled, refreshDiseases]);

  useEffect(() => {
    if (enabled && selectedDiseaseId) refreshOverlays(selectedDiseaseId);
  }, [enabled, selectedDiseaseId, refreshOverlays]);

  useEffect(() => {
    if (selectedDisease) {
      setPinDraft({
        name: selectedDisease.name || "",
        description: selectedDisease.description || "",
        disease_label: selectedDisease.name || "",
        severity: selectedDisease.severity || "mild",
      });
    }
  }, [selectedDisease]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const pollOverlay = useCallback(
    (overlayId) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const res = await getPathologyOverlayCall(overlayId);
          const ov = normalizePathologyOverlay(res?.overlay);
          if (!ov) return;
          setActiveOverlay(ov);
          setOverlays((prev) => {
            const idx = prev.findIndex((o) => o.id === ov.id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = ov;
              return next;
            }
            return [...prev, ov];
          });
          if (
            ov.status === "preview" ||
            ov.status === "failed" ||
            ov.status === "accepted"
          ) {
            stopPolling();
            refreshOverlays(selectedDiseaseId);
          }
        } catch (_) {
          /* keep polling */
        }
      }, 2000);
    },
    [stopPolling, refreshOverlays, selectedDiseaseId]
  );

  const handleCreateDisease = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await createPathologyDiseaseCall({
        slide_id: slideId,
        name: newName.trim(),
        description: newDescription.trim(),
        severity: newSeverity,
      });
      setNewName("");
      setNewDescription("");
      await refreshDiseases();
      if (res?.disease?.id) setSelectedDiseaseId(res.disease.id);
    } catch (e) {
      setError(e?.message || "Could not create disease");
    } finally {
      setLoading(false);
    }
  };

  const handlePublishDisease = async () => {
    if (!selectedDiseaseId) return;
    setLoading(true);
    try {
      await updatePathologyDiseaseCall(selectedDiseaseId, { status: "published" });
      await refreshDiseases();
    } catch (e) {
      setError(e?.message || "Publish failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDisease = async () => {
    if (!selectedDiseaseId || !window.confirm("Delete this disease and all AI overlays?")) {
      return;
    }
    setLoading(true);
    try {
      await deletePathologyDiseaseCall(selectedDiseaseId);
      setSelectedDiseaseId("");
      setActiveOverlay(null);
      await refreshDiseases();
      setOverlays([]);
    } catch (e) {
      setError(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    const map = mapRef?.current;
    if (!map || !roiBounds || !selectedDiseaseId) {
      setError("Select a disease and draw a rectangle on the slide first.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const capture = capturePixelsFromBounds(map, roiBounds);
      if (
        capture.capture_pixels.width < 80 ||
        capture.capture_pixels.height < 80
      ) {
        setError(
          "ROI is too small — zoom in on tissue and draw a larger rectangle (at least 80×80 px)."
        );
        return;
      }
      if ((capture.capture_zoom ?? 0) < 3) {
        setError(
          "Zoom in closer before generating (recommended at least zoom level 3)."
        );
        return;
      }
      const slideRoi = leafletBoundsToPixelRoi(
        map,
        roiBounds,
        maxZoom ?? map.getMaxZoom()
      );
      const res = await generatePathologyOverlayCall({
        disease_id: selectedDiseaseId,
        slide_id: slideId,
        capture_zoom: capture.capture_zoom,
        capture_pixels: capture.capture_pixels,
        crop_width: capture.capture_pixels.width,
        crop_height: capture.capture_pixels.height,
        roi: slideRoi,
        bounds_json: boundsToJson(roiBounds),
        max_zoom: maxZoom,
        prompt: customPrompt.trim() || undefined,
      });
      const ov = normalizePathologyOverlay(res?.overlay);
      setActiveOverlay(ov);
      if (ov?.id) pollOverlay(ov.id);
    } catch (e) {
      setError(e?.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!activeOverlay?.id) return;
    setLoading(true);
    setError("");
    try {
      const body = {};
      if (customPrompt.trim()) body.prompt = customPrompt.trim();
      const map = mapRef?.current;
      if (map && roiBounds) {
        const capture = capturePixelsFromBounds(map, roiBounds);
        body.capture_zoom = capture.capture_zoom;
        body.capture_pixels = capture.capture_pixels;
        body.crop_width = capture.capture_pixels.width;
        body.crop_height = capture.capture_pixels.height;
        body.bounds_json = boundsToJson(roiBounds);
      }
      const res = await regeneratePathologyOverlayCall(activeOverlay.id, body);
      const ov = normalizePathologyOverlay(res?.overlay);
      setActiveOverlay(ov);
      if (ov?.id) pollOverlay(ov.id);
    } catch (e) {
      setError(e?.message || "Regenerate failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!activeOverlay?.id) return;
    setLoading(true);
    setError("");
    try {
      const pins = [
        {
          name: selectedDisease?.name || pinDraft.name.trim() || "Disease",
          description:
            selectedDisease?.description || pinDraft.description.trim() || "",
          disease_label: selectedDisease?.name || pinDraft.disease_label || "",
          severity: selectedDisease?.severity || pinDraft.severity,
          pin_x: 50,
          pin_y: 50,
        },
      ];
      const res = await acceptPathologyOverlayCall(activeOverlay.id, { pins });
      setActiveOverlay(null);
      setRoiDrawActive(false);
      setRoiBounds(null);
      await refreshOverlays(selectedDiseaseId);
      if (res?.overlay) {
        setOverlays((prev) => {
          const accepted = normalizePathologyOverlay(res.overlay);
          if (!accepted) return prev;
          const idx = prev.findIndex((o) => o.id === accepted.id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = accepted;
            return next;
          }
          return [...prev, accepted];
        });
      }
    } catch (e) {
      setError(e?.message || "Accept failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOverlay = async (overlayId) => {
    if (!overlayId) return;
    const label =
      activeOverlay?.id === overlayId && activeOverlay?.status === "preview"
        ? "Discard this preview overlay?"
        : "Delete this overlay from the slide?";
    if (!window.confirm(label)) return;
    setLoading(true);
    setError("");
    try {
      await deletePathologyOverlayCall(overlayId);
      if (activeOverlay?.id === overlayId) {
        setActiveOverlay(null);
      }
      await refreshOverlays(selectedDiseaseId);
    } catch (e) {
      setError(e?.message || "Delete overlay failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDiscardPreview = () => {
    if (activeOverlay?.id) {
      void handleDeleteOverlay(activeOverlay.id);
    } else {
      setActiveOverlay(null);
    }
  };

  const acceptedOverlays = overlays.filter((o) => o.status === "accepted");
  const previewOverlay =
    overlays.find((o) => o.status === "preview" && o.overlay_url) ||
    (activeOverlay?.status === "preview"
      ? normalizePathologyOverlay(activeOverlay)
      : null);
  const isGenerating =
    activeOverlay?.status === "pending" ||
    activeOverlay?.status === "generating";

  return {
    diseases,
    selectedDiseaseId,
    setSelectedDiseaseId,
    selectedDisease,
    overlays,
    loading,
    error,
    newName,
    setNewName,
    newDescription,
    setNewDescription,
    newSeverity,
    setNewSeverity,
    roiDrawActive,
    setRoiDrawActive,
    roiBounds,
    setRoiBounds,
    activeOverlay,
    previewOpacity,
    setPreviewOpacity,
    customPrompt,
    setCustomPrompt,
    pinDraft,
    setPinDraft,
    acceptedOverlays,
    previewOverlay,
    isGenerating,
    handleCreateDisease,
    handlePublishDisease,
    handleDeleteDisease,
    handleGenerate,
    handleRegenerate,
    handleAccept,
    handleDeleteOverlay,
    handleDiscardPreview,
  };
}
